import { AlertTriangle, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Label } from "@/components/ui/label";

import type { BaseFieldProps } from "../types";

import { createFieldComponent } from "../utils/fieldFactory";

/**
 * Динамическая группа полей с ИИ (t_b8ffa1fc): область состояния генерации
 * показывается внутри самой группы. Статичные поля отображаются всегда,
 * динамические появляются под ними после ответа сервера (лимит ожидания —
 * 1 минута, затем fallback: форму можно отправить без ИИ-полей).
 */
export function FieldGroupField({
	field,
	value,
	onChange,
	error,
	disabled,
	formId,
	builderMode,
	aiLoading,
	aiDegraded,
	aiDegradedReason,
}: BaseFieldProps) {
	const t = useTranslations("forms");
	const isAiGroup = field.settings?.aiEnabled === true;
	const getGroupFields = () => {
		const all = field.settings?.groupFields || [];
		// В превью конструктора показываем только статичные поля —
		// динамические генерируются на публичной форме (шаг 2+).
		if (builderMode && isAiGroup) {
			return all.filter((groupField: any) => !groupField.id?.includes("_dyn_"));
		}
		return all;
	};

	const getGroupLayout = () => field.settings?.groupLayout || "horizontal";

	const getGroupSpacing = () => field.settings?.groupSpacing || "normal";

	const getGroupColumns = () => field.settings?.groupColumns || 2;

	const getSpacingClassName = () => {
		const spacing = getGroupSpacing();
		switch (spacing) {
			case "compact":
				return "gap-2";
			case "relaxed":
				return "gap-6";
			default:
				return "gap-4";
		}
	};

	const getLayoutClassName = () => {
		const layout = getGroupLayout();

		if (layout === "vertical") {
			return "flex flex-col";
		}

		const columns = getGroupColumns();
		switch (columns) {
			case 3:
				return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
			case 4:
				return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
			default:
				return "grid grid-cols-1 md:grid-cols-2";
		}
	};

	const getContainerClassName = () => {
		const layoutClass = getLayoutClassName();
		const spacingClass = getSpacingClassName();
		return `${layoutClass} ${spacingClass}`;
	};

	const getFieldValue = (fieldId: string) => value?.[fieldId];

	const getFieldError = (fieldId: string) => {
		if (typeof error === "object" && error !== null && fieldId in error) {
			return (error as Record<string, string | undefined>)[fieldId];
		}
		return;
	};

	const handleGroupFieldChange = (fieldId: string, fieldValue: any) => {
		const currentValues = value || {};
		onChange({
			...currentValues,
			[fieldId]: fieldValue,
		});
	};

	const renderGroupFieldLabel = (groupField: any) => {
		if (!groupField.label) {
			return null;
		}

		return (
			<Label className="font-medium text-foreground text-sm">
				{groupField.label}
				{groupField.required && (
					<span className="ml-1 text-destructive">*</span>
				)}
			</Label>
		);
	};

	const renderGroupField = (groupField: any) => {
		const fieldValue = getFieldValue(groupField.id);
		const fieldError = getFieldError(groupField.id);

		return (
			<div className="flex-1" key={groupField.id}>
				<div className="flex flex-col gap-2">
					{renderGroupFieldLabel(groupField)}
					{createFieldComponent(
						groupField,
						fieldValue,
						(fieldValue) => handleGroupFieldChange(groupField.id, fieldValue),
						fieldError,
						undefined,
						disabled,
						formId
					)}
				</div>
			</div>
		);
	};

	const groupFields = getGroupFields();
	const showSkeleton = builderMode !== true && aiLoading === true;
	const showDegraded = builderMode !== true && aiDegraded === true;
	const degradedMessage =
		aiDegradedReason === "timeout"
			? t("aiGroup.timeout")
			: t("aiGroup.degraded");

	return (
		<div className={getContainerClassName()}>
			{groupFields.map(renderGroupField)}
			{showSkeleton && (
				<div
					aria-busy="true"
					aria-live="polite"
					className="col-span-full flex flex-col gap-3 rounded-lg border border-border border-dashed p-4"
				>
					<div className="flex items-center gap-2">
						<Loader2
							aria-hidden="true"
							className="size-4 animate-spin text-muted-foreground"
						/>
						<span className="text-muted-foreground text-sm">
							{t("aiGroup.generating")}
						</span>
					</div>
					<div className="flex flex-col gap-2">
						<div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
						<div className="h-9 w-full animate-pulse rounded bg-muted" />
						<div className="h-4 w-1/4 animate-pulse rounded bg-muted" />
						<div className="h-9 w-full animate-pulse rounded bg-muted" />
					</div>
				</div>
			)}
			{showDegraded && (
				<div
					className="col-span-full flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 text-sm dark:text-amber-400"
					role="status"
				>
					<AlertTriangle
						aria-hidden="true"
						className="mt-0.5 size-4 shrink-0"
					/>
					<span>{degradedMessage}</span>
				</div>
			)}
		</div>
	);
}
